<template>
  <div
    ref="tree"
    class="rt-tree"
  >
    <div
      class="rt-tree-header"
      :class="{ 'has-add-button': showAdd || $slots.addButton }"
    >
      <rt-search
        v-if="search"
        v-model="searchValue"
        :more="false"
        :placeholder="searchPlaceholder"
      ></rt-search>
      <slot
        v-if="showAdd || $slots.addButton"
        name="addButton"
      >
        <rt-button
          icon-name="rt-base-plus"
          color-type="info"
          type="plain"
          @click="addClickHandler"
        />
      </slot>
    </div>
    <el-tree
      ref="elTree"
      :data="dataSource"
      :node-key="nodeKey"
      :empty-text="emptyText"
      :props="props"
      :show-checkbox="showCheckbox"
      :default-expand-all="defaultExpandAll"
      :expand-on-click-node="expandOnClickNode"
      :check-on-click-node="checkOnClickNode"
      :default-checked-keys="defaultCheckedKeys"
      :default-expanded-keys="defaultExpandedKeys"
      :draggable="draggable"
      :allow-drag="allowDrag"
      :allow-drop="allowDrop"
      :accordion="accordion"
      :filter-node-method="filterNode"
      @node-click="nodeClick"
      @check="check"
      @check-change="checkChange"
      @node-expand="nodeExpand"
      @node-collapse="nodeCollapse"
      @node-drag-start="nodeDragStart"
      @node-drag-enter="nodeDragEnter"
      @node-drag-leave="nodeDragLeave"
      @node-drag-over="nodeDragOver"
      @node-drag-end="nodeDragEnd"
      @node-drop="nodeDrop"
    >
      <!-- eslint-disable -->
      <span
        slot-scope="{ node, data }"
        :class="{
          'has-leaf-avatar': data.avatar !== false && node.isLeaf && leafAvatar
        }"
        class="rt-tree-node"
      >
        <span class="rt-tree-node-left">
          <rt-avatar
            v-if="data.avatar !== false && node.isLeaf && leafAvatar"
            :src="data.avatarSrc"
            size="small"
            type="info"
            effect="light"
            >{{ node.label.charAt(0) }}</rt-avatar
          >

          <slot v-else :node="node" :data="data" name="icon">
            <rt-icon
              v-if="data.rtIconName || rtIconName"
              :name="data.rtIconName || rtIconName"
              :size="
                node.isLeaf
                  ? data.rtLeafIconSize || rtLeafIconSize
                  : data.rtIconSize || rtIconSize
              "
              :color="data.rtIconColor || rtIconColor"
            />
          </slot>

          <span>{{ node.label }}</span>
          <rt-tag
            v-if="rtTag !== false && data.rtTag"
            border
            size="small"
            color-type="primary"
            >{{ data[data.rtTag || rtTag] || data['rtTag'] }}</rt-tag
          >
          <slot name="node-right" :node="node" :data="data"></slot>
        </span>

        <span class="rt-tree-node-right">
          <slot :node="node" :data="data"></slot>
        </span>
      </span>
      <template slot="node-desc" slot-scope="scope">
        <slot name="node-desc" :node="scope.node"></slot>
      </template>
    </el-tree>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import ElTree from 'vue-xpc/src/element-ui/packages/tree';
const methods = [
  'filter',
  'updateKeyChildren',
  'getCheckedNodes',
  'setCheckedNodes',
  'getCheckedKeys',
  'setCheckedKeys',
  'setChecked',
  'getHalfCheckedNodes',
  'getHalfCheckedKeys',
  'getCurrentKey',
  'getCurrentNode',
  'setCurrentKey',
  'setCurrentNode',
  'getNode',
  'insertBefore',
  'insertAfter'
];

export default {
  name: 'RtTree',
  components: { ElTree },
  props: {
    /**
     * @public 源数据
     */
    data: {
      type: Array
    },
    /**
     * @public 空数据展示文字
     */
    emptyText: {
      type: String,
      default: '暂无数据'
    },
    /**
     * @public 区分节点的唯一值，如果使用remove等方法必须设置
     */
    nodeKey: String,
    search: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: '请搜索...'
    },
    /**
     * @public 自定义xpc的icon名称
     */
    rtIconName: String,
    /**
     * @public 自定义xpcicon的尺寸
     */
    rtIconSize: {
      type: Number,
      default: 24
    },
    /**
     * @public 自定义xpcicon的尺寸
     */
    rtLeafIconSize: {
      type: Number,
      default: 16
    },
    /**
     * @public 自定义xpcicon的颜色
     */
    rtIconColor: {
      type: String,
      default: function () {
        return this.SCSS_VARIABLES.COLOR_INFO;
      }
    },
    /**
     * @public 叶节点是否展示头像
     */
    leafAvatar: {
      type: Boolean,
      default: false
    },
    /**
     * @public 是否展示标签
     */
    rtTag: {
      type: [Boolean, String],
      default: false
    },
    /**
     * @public 是否默认展开所有节点
     */
    defaultExpandAll: Boolean,
    /**
     * @public 是否在点击节点的时候自动 展开/收起 节点，默认true，如果为false，则只有点击展开箭头时才能展开收起
     */
    expandOnClickNode: {
      type: Boolean,
      default: true
    },
    /**
     * @public 是否在点击节点的时候选中节点，默认值为 false，即只有在点击复选框时才会选中节点
     */
    checkOnClickNode: Boolean,
    /**
     * @public 默认选中的节点
     */
    defaultCheckedKeys: Array,
    /**
     * @public 默认展开的节点
     */
    defaultExpandedKeys: Array,
    /**
     * @public 是否有复选框
     */
    showCheckbox: {
      type: Boolean,
      default: false
    },
    /**
     * @pubilc 是否支持拖拽
     */
    draggable: {
      type: Boolean,
      default: false
    },
    /**
     * @public 拖拽时判断节点能否被拖拽
     */
    allowDrag: Function,
    /**
     * @public 拖拽时判断节点能否被放置
     */
    allowDrop: Function,
    /**
     * @public 手风琴模式
     */
    accordion: {
      type: Boolean,
      default: false
    },
    indent: {
      type: Number,
      default: 18
    },
    iconClass: String,
    props: {
      type: Object,
      default () {
        return {
          children: 'children',
          label: 'label',
          disabled: 'disabled'
        };
      }
    },
    showAdd: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      searchValue: '',
      dataSource: []
    };
  },
  watch: {
    data (val) {
      this.dataSource = cloneDeep(val);
    },
    searchValue (val) {
      this.$refs.elTree.filter(val);
    }
  },

  mounted () {
    this.dataSource = cloneDeep(this.data);
    this.$nextTick(() => {
      // 将 elementui tree 组件原生方法附加在 rtTree 上面
      methods.forEach(method => {
        this[method] = this.$refs.elTree[method];
      });
    });
  },
  methods: {
    nodeClick (data, el, com) {
      this.$emit('node-click', data, el, com);
    },
    check (data, checked) {
      this.$emit('check', data, checked);
    },
    checkChange (data, checked, childrenChecked) {
      this.$emit('check-change', data, checked, childrenChecked);
    },
    nodeExpand (data, el, com) {
      this.$emit('node-expand', data, el, com);
    },
    nodeCollapse (data, el, com) {
      this.$emit('node-collapse', data, el, com);
    },
    // 节点开始拖拽时触发的事件,共两个参数，依次为：被拖拽节点对应的 Node、event
    nodeDragStart (el, event) {
      this.$emit('node-drag-start', el, event);
    },
    // 拖拽进入其他节点时触发的事件,共三个参数，依次为：被拖拽节点对应的 Node、所进入节点对应的 Node、event
    nodeDragEnter (el, enterEl, event) {
      this.$emit('node-drag-enter', el, enterEl, event);
    },
    // 拖拽离开某个节点时触发的事件,共三个参数，依次为：被拖拽节点对应的 Node、所离开节点对应的 Node、event
    nodeDragLeave (el, outEl, event) {
      this.$emit('node-drag-leave', el, outEl, event);
    },
    // 在拖拽节点时触发的事件（类似浏览器的 mouseover 事件）,共三个参数，依次为：被拖拽节点对应的 Node、当前进入节点对应的 Node、event
    nodeDragOver (el, enterEl, event) {
      this.$emit('node-drag-over', el, enterEl, event);
    },
    // 拖拽结束时（可能未成功）触发的事件,共四个参数，依次为：被拖拽节点对应的 Node、结束拖拽时最后进入的节点（可能为空）、被拖拽节点的放置位置（before、after、inner）、event
    nodeDragEnd (el, enterEl, location, event) {
      this.$emit('node-drag-end', el, enterEl, location, event);
    },
    nodeDrop (el, enterEl, location, event) {
      this.$emit('node-drop', el, enterEl, location, event);
    },
    filterNode (value, data, node) {
      if (!value) return true;
      return node.label.indexOf(value) !== -1;
    },
    addClickHandler (e) {
      this.$emit('add-click', e, this);
    },
    append (data, parentNode) {
      if (!parentNode) {
        parentNode = this.$refs.elTree.getNode(this.dataSource);
      } else {
        parentNode = this.$refs.elTree.getNode(parentNode);
      }
      this.$refs.elTree.append(data, parentNode);
      this.$refs.elTree.setCurrentNode(data, true);
      if (parentNode) {
        parentNode.expanded = true;
      }
    },
    remove (data) {
      this.$refs.elTree.remove(data);
    },
    update (newData, oldData) {
      oldData = { ...oldData, ...newData };
      this.$refs.elTree.update(oldData);
    },
    insertBefore (data, refNode) {
      this.$refs.elTree.insertBefore(data, refNode);
    },

    insertAfter (data, refNode) {
      this.$refs.elTree.insertAfter(data, refNode);
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtTree.vue?14018a8c